import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FiltersComponent } from '../../shared/tablefilters/filters/filters.component';
import { TableComponent } from '../../shared/tablefilters/table/table.component';
import { TableFilterService } from '../../shared/tablefilters/table-filter.service';
import { Column } from '../../shared/tablefilters/column.model';
import { TableDetails } from '../../shared/tablefilters/table-details.model';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { ValidationConstant } from '../../../../../constants/validation.constant';
import { TranslateModule } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { AddUser } from '../add-user/add-user';
import {
  CommonService,
  Exception,
  StorageService,
  StorageType,
  ToasterService,
} from '@anddone/ui-utilities';
import { UserService } from '../../../../../services/user-service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DatePipe, Location } from '@angular/common';
import { UserAuthenticationType } from '../../../../../enum/UserAuthenticationType.enum';
import { MessageConstants } from '../../../../../constants/message.constant';
import { AllyCommonService } from '../../../../../services/ally-common-service';
import { RollApi } from '../roll-api/roll-api';

@Component({
  selector: 'app-find-users',
  imports: [TableComponent, FiltersComponent, FormsModule, TranslateModule],
  templateUrl: './find-users.html',
  styleUrl: './find-users.scss',
  providers: [DatePipe],
})
export class FindUsers implements OnInit, OnDestroy {
  keyModalRef: any;
  private destroy$ = new Subject<void>();
  pager: any = {};
  isFirstLoad = true;
  searchParamsData: any = {};
  tableActionSub!: Subscription;
  searchInputSub!: Subscription;
  userList: any[] = [];

  /*** Defined table details & columns ****/
  tableDetails: TableDetails = {
    tableId: 'find-users-table',
    searchPlaceholder: 'Username, Email',
    allowRowClick: true,
    rowClickURI: '/ally/user',
    tableTitle: 'Users',
    defaultItemPerPage: ValidationConstant.defaultPageSize,
    sendDefault90DayFilter: false,
  };
  columns: Column[] = [
    {
      name: 'Username',
      sortState: 'none',
      isSortable: true,
      display: 'normal',
      responseKey: 'userName',
      requestKey: 'UserName',
      filter: {
        type: 'string',
        validation: ['required'],
      },
    },
    {
      name: 'Email',
      sortState: 'none',
      isSortable: true,
      display: 'normal',
      responseKey: 'email',
      requestKey: 'Email',
      filter: {
        type: 'email',
        validation: ['required'],
        // validation: [ 'email' ] // Turn this on if you only want to allow full email addresses to be searched rather than partial matches.
      },
    },
    {
      name: 'Auth Types',
      sortState: 'none',
      isSortable: true,
      display: 'normal',
      responseKey: 'userAuthenticationType',
      requestKey: 'UserAuthenticationType',
      filter: {
        type: 'multipleChoice',
        options: [
          { label: 'Basic Auth', key: 'BearerOnly', checked: false },
          { label: 'API Key', key: 'APIOnly', checked: false },
        ],
      },
    },
    {
      name: 'Status',
      sortState: 'none',
      isSortable: true,
      display: 'status',
      responseKey: 'status',
      requestKey: 'Status',
      statusColors: {
        Active: 'green',
        Inactive: 'red',
      },
      filter: {
        type: 'multipleChoice',
        options: [
          { label: 'Active', key: 'Active', checked: false },
          { label: 'Inactive', key: 'Inactive', checked: false },
        ],
      },
    },
    {
      name: 'Actions',
      sortState: 'none',
      isSortable: false,
      display: 'actions',
      responseKey: 'operations',
      requestKey: null,
      filter: undefined,
    },
  ];
  loggedInUserData: any;
  config = {
    searchInput: {
      required: { name: ValidationConstant.user.searchInput.name },
      minlength: {
        name: ValidationConstant.user.searchInput.name,
        min: ValidationConstant.user.searchInput.minLength.toString(),
      },
    },
  };

  public allyCommonService = inject(AllyCommonService);
  public commonService = inject(CommonService);
  private toasterService = inject(ToasterService);
  private storageService = inject(StorageService);
  private userService = inject(UserService);
  private modalService = inject(NgbModal);
  private location = inject(Location);
  private tableFilterService = inject(TableFilterService);

  ngOnInit(): void {
    this.loggedInUserData = this.getLoggedInData();
    this.commonService.getAllCountryList()?.subscribe((countryRes) => {
      this.allyCommonService.setCountryList(countryRes);
    });
    this.commonService.getStateList()?.subscribe((stateRes) => {
      this.allyCommonService.setStateList(stateRes);
    });

    this.userService.currentUserAddStatus.pipe(takeUntil(this.destroy$)).subscribe((status) => {
      if (status === true) {
        this.findUser(1);
      }
    });
    this.userService.userDetailUpdate.pipe(takeUntil(this.destroy$)).subscribe((status) => {
      if (status === true) {
        this.findUser(1);
      }
    });
    this.location.subscribe(() => {
      if (this.keyModalRef) {
        this.keyModalRef.close();
      }
    });

    /*** Table Filter subscriptions ***/
    this.tableFilterService.init(this.columns, this.tableDetails);

    this.tableActionSub = this.tableFilterService.tableActionEvent.subscribe((operationObject) => {
      this.onUserOperationClick(operationObject.operation, operationObject.row);
    });

    this.searchInputSub = this.tableFilterService.searchInput
      .pipe(takeUntil(this.destroy$))
      .subscribe((searchInput) => {
        this.searchParamsData = searchInput.searchParams;
        this.pager = searchInput.pager;
        /*** Assign the method that manages your API call here. ***/
        this.findUser(this.pager.currentPage);
      });

    this.tableFilterService.registerSubscriptions(this.searchInputSub, this.tableActionSub);
    /*** End of Table filter subscription handling ***/
  }

  /*** Defined operations to execute from table events */
  onUserOperationClick(operation: any, user: any) {
    if (operation.key === 'activate') {
      this.activateUser(user);
    } else if (operation.key === 'deactivate') {
      this.deactivateUser(user);
    } else if (operation.key == 'rollAPIKey') {
      this.getAPIKeys(user.id);
    }
  }

  /* user api call function */
  findUser(pageNumber: number) {
    this.commonService.scrollTop('userTable');
    if (pageNumber <= 0) {
      return;
    }
    if (this.pager.totalPages > 0) {
      if (pageNumber > this.pager.totalPages) {
        return;
      }
    }

    this.allyCommonService.setIsLoader(true);
    /*** prepared the searchParamsData object to be sent to the API call ***/
    this.searchParamsData = this.tableFilterService.prepareSearchParamsForAPICall(pageNumber);
    if (this.isFirstLoad) {
      this.searchParamsData.SortField = 'CreatedOn';
      this.isFirstLoad = false;
    }

    this.userService
      .getAllyUsers(this.loggedInUserData.id, this.searchParamsData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.allyCommonService.setIsLoader(false);
          if (res.data.length == 0) {
            this.userList = [];
            this.tableFilterService.setPager(res, pageNumber, this.pager);

            /*** Update the tableData toshow no results  ***/
            this.tableDetails.tableData = [];
            this.tableFilterService.setTableDetails(this.tableDetails);
          } else {
            this.tableFilterService.setPager(res, pageNumber, this.pager);
            res.data.forEach((ele: any) => {
              ele.userName = ele.userName ? ele.userName : MessageConstants.common.noData;
              ele.email = ele.contact.email ? ele.contact.email : MessageConstants.common.noData;
              ele.accounts = this.loggedInUserData.name;

              /*** Configure operations for table actions row ***/
              ele.operations = [];
              if (ele.isActive) {
                ele.operations.push({ key: 'deactivate', value: 'Deactivate' });
                if (
                  ele.userAuthenticationType != UserAuthenticationType.BearerOnly &&
                  ele.userAuthenticationType != 1
                ) {
                  // Basic Auth Users shall not have Roll API Key as an action
                  ele.operations.push({ key: 'rollAPIKey', value: 'Roll API Key' });
                }
              } else {
                ele.operations.push({ key: 'activate', value: 'Activate' });
              }
            });
            this.userList = res.data;
            this.tableDetails.tableData = this.userList || [];
            this.parseTableData(this.tableDetails.tableData);

            /*** Set the table details to include data to be displayed and pagination data and send to tablefilter service */
            this.tableFilterService.setTableDetails(this.tableDetails);
          }
        },
        error: (e: any) => {
          this.allyCommonService.setIsLoader(false);
          const toastMessage = Exception.exceptionMessage(e);
          this.toasterService.showError(toastMessage.join(', '), 'full');
        },
      });
  }

  /*** loop through each row and apply table specific requirements ***/
  parseTableData(tableData: any) {
    for (const row of tableData) {
      this.overrideTableDataValues(row);
      this.updateAuthType(row);
      //TODO add actions
    }
  }

  /***  Optional table data customization. If you need to change the values to be displayed, you can do that here.  ***/
  overrideTableDataValues(row: any) {
    if (row.isActive == true) {
      row.status = 'Active';
    } else {
      row.status = 'Inactive';
    }
  }

  /* update Auth Type key value to the desired enum result */
  updateAuthType(row: any) {
    let temp;
    if (row.userAuthenticationType == UserAuthenticationType.NA || row.userAuthenticationType == 0)
      temp = 'NA';
    if (
      row.userAuthenticationType == UserAuthenticationType.BearerOnly ||
      row.userAuthenticationType == 1
    )
      temp = 'Basic Auth';
    if (
      row.userAuthenticationType == UserAuthenticationType.APIOnly ||
      row.userAuthenticationType == 2
    )
      temp = 'API Key';
    if (
      row.userAuthenticationType == UserAuthenticationType.BearerAndAPI ||
      row.userAuthenticationType == 3
    )
      temp = 'Basic Auth, API Key';
    row.userAuthenticationType = temp;
  }

  getAPIKeys(userId: string) {
    this.allyCommonService.setIsLoader(true);
    const reqObj = {
      allyId: this.loggedInUserData?.id,
      userId: userId,
    };
    //call API to get user details by ID
    this.userService
      .getKeys(reqObj)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: any) => {
          response.userId = userId;
          this.userService.setAPIKey(response);
          this.allyCommonService.setIsLoader(false);
          this.showApiModal();
        },
        (error: any) => {
          this.allyCommonService.setIsLoader(false);
          const toastMessage = Exception.exceptionMessage(error);
          this.toasterService.showError(toastMessage.join(', '), 'full');
        }
      );
  }

  showApiModal() {
    this.keyModalRef = this.modalService.open(RollApi, { scrollable: true });
  }

  getLoggedInData() {
    return JSON.parse(this.storageService.get(StorageType.Session, 'userDetails') ?? '{}');
  }
  // Displaying the Public key in modal popup
  showKeyModal() {
    this.keyModalRef = this.modalService.open(AddUser, { scrollable: true });
  }

  /*activate User */
  activateUser(user: any) {
    this.allyCommonService.setIsLoader(true);
    const currentPage = this.pager.currentPage;
    this.userService.activateUser(user.parentId, user.id).subscribe({
      next: (_res: any) => {
        this.allyCommonService.setIsLoader(false);
        this.toasterService.showSuccess(MessageConstants.common.userActivate, 'full');
        this.findUser(currentPage);
      },
      error: (e) => {
        this.allyCommonService.setIsLoader(false);
        const toastMessage = Exception.exceptionMessage(e);
        this.toasterService.showError(toastMessage.join(', '), 'full');
      },
    });
  }

  /*deactivate User */
  deactivateUser(user: any) {
    this.allyCommonService.setIsLoader(true);
    const currentPage = this.pager.currentPage;
    this.userService.deactivateUser(user.parentId, user.id).subscribe({
      next: (_res: any) => {
        this.allyCommonService.setIsLoader(false);
        this.toasterService.showSuccess(MessageConstants.common.userDeactivated, 'full');
        this.findUser(currentPage);
      },
      error: (e) => {
        this.allyCommonService.setIsLoader(false);
        const toastMessage = Exception.exceptionMessage(e);
        this.toasterService.showError(toastMessage.join(', '), 'full');
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(); // Triggers takeUntil
    this.destroy$.complete(); // Final cleanup
  }
}
